<template>
  <div :class="['eventmap-page', {
      'eventmap-page--fullscreen': fullScreenMode,
      'eventmap-page--kiosk': mode === 'kiosk'
    }]">
    <div class="eventmap-page__container">
      <EventmapSearchBar
        v-model:searchText="searchText"
        v-model:modalState="modalState"
        v-model:eventmap="eventmap"
        v-model:selectedLocation="selectedLocation"
        v-model:routeEnd="routeEnd"
        :mode="mode"
        @onLocationSelected="onLocationSelected"
      />

      <EventmapRoutePlanner
        v-model:modalState="modalState"
        v-model:routeStart="routeStart"
        v-model:routeEnd="routeEnd"
        v-model:activePath="activePath"
        v-model:selectedLocation="selectedLocation"
        :mode="mode"
        @onLocationSelected="onLocationSelected"
      />

      <div
        v-if="modalState === 'set-start'"
        class="eventmap-page__search eventmap-page__search--open"
      >
        <EventmapLocationSearchList
          v-model:routeEnd="routeEnd"
          v-model:modalState="modalState"
          v-model:selectedLocation="routeEnd"
          :locations="eventmap.expanded_locations"
          searchInputPlaceholder="Start"
          autofocus
          showCloseSearchButton
          :mode="mode"
          @onLocationSelected="(location) => setStartPoint(location)"
        />
      </div>

      <div
        v-if="modalState === 'set-destination'"
        class="eventmap-page__search eventmap-page__search--open"
      >
        <EventmapLocationSearchList
          v-model:routeEnd="routeEnd"
          v-model:modalState="modalState"
          v-model:selectedLocation="routeStart"
          :locations="eventmap.expanded_locations"
          searchInputPlaceholder="Destination"
          autofocus
          showCloseSearchButton
          :mode="mode"
          @onLocationSelected="(location) => setEndPoint(location)"
        />
      </div>

      <EventmapLocationDetailsModal
        v-model:modalState="modalState"
        v-model:routeStart="routeStart"
        v-model:routeEnd="routeEnd"
        v-model:activePath="activePath"
        v-model:selectedLocation="selectedLocation"
        v-model:searchText="searchText"
        v-model:eventmap="eventmap"
        v-model:eventmapSVG="eventmapSVG"
        :mode="mode"
        @onLocationSelected="onLocationSelected"
        :generateQrCodeUrl="generateQrCodeUrl"
      />

      <div
        class="eventmap-page__eventmap"
        style="position: relative;"
      >
        <EventmapSVG
          v-if="eventmap?.id"
          ref="eventmapSVG"
          :eventmap="eventmap"
          :selectedLocation="selectedLocation"
          :lockSelected="false"
          @locationClicked="locationClicked"
          @locationDblClicked="previewLocation"
          flashSelectedLocation
          :showPaths="true"
          overridePathColor="transparent"
          :activePath="activePath"
          @svgReady="zoomSoon"
        />
      </div>
      <EventmapNavigationControls
        v-model:eventmapSVG="eventmapSVG"
        v-model:fullScreenMode="fullScreenMode"
        :mode="mode"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import EventmapSearchBar from '~/Components/Frontend/Eventmap/EventmapSearchBar.vue';
import EventmapNavigationControls from '~/Components/Frontend/Eventmap/EventmapNavigationControls.vue';
import EventmapLocationDetailsModal from '~/Components/Frontend/Eventmap/EventmapLocationDetailsModal.vue';
import EventmapRoutePlanner from '~/Components/Frontend/Eventmap/EventmapRoutePlanner.vue';
import useEventmapHandlers from '~/Components/Frontend/Eventmap/Composables/useEventmapHandlers';

const props = defineProps({
  eventmapId: {
    type: Number,
    default: null,
  },
  locationId: {
    type: Number,
    default: null,
  },
  kioskLocationId: {
    type: Number,
    default: null,
  },
  navigateFrom: {
    type: Object,
    default: null,
  },
  navigateTo: {
    type: Object,
    default: null,
  },
  showUrl: String,
  generateQrCodeUrl: String,
  mode: {
    type: String,
    default: 'platform',
  },
});

const modalState = ref('normal');
const searchText = ref('');
const selectedLocation = ref(null);
const routeStart = ref(null);
const routeEnd = ref(null);
const fullScreenMode = ref(false);
const eventmapSVG = ref(null);
const eventmap = ref(null);
const activePath = ref(null);

const {
  onLocationSelected,
  setStartPoint,
  setEndPoint,
  previewLocation,
  zoomSoon,
} = useEventmapHandlers({
  modalState,
  selectedLocation,
  routeStart,
  routeEnd,
  eventmapSVG,
  eventmap,
  props,
});

const locationClicked = function (location) {
  if (location.has_multiple_tenants) {
    searchText.value = location.number;
  } else {
    searchText.value = '';
  }
  onLocationSelected(location);
};

onMounted(() => {
  axios
    .get(props.showUrl)
    .then((response) => {
      eventmap.value = response.data.eventmap;
      if (props.navigateFrom && props.navigateTo) {
        const locations = eventmap.value?.locations || [];
        const start = locations.find((loc) => loc.id === props.navigateFrom.id);
        const end = locations.find((loc) => loc.id === props.navigateTo.id);
        if (start && end) {
          routeStart.value = start;
          routeEnd.value = end;
        }
      }
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching eventmap data:', error);
    });
});
</script>
